import PageContainer from '../../../../../components/layout/PageContainer';

const SubscriptionSkeleton = () => {
  return (
    <PageContainer className="font-sans text-slate-800 pb-12">
      {/* 1. Header & Billing Toggle Skeleton */}
      <section className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <div className="h-8 sm:h-9 w-60 sm:w-80 bg-slate-200 rounded-xl animate-pulse" />
          <div className="h-4 w-72 sm:w-96 max-w-full bg-slate-100 rounded-lg mt-2.5 animate-pulse" />
        </div>

        {/* Segmented Pill Toggle Skeleton */}
        <div className="self-start md:self-auto inline-flex items-center p-1 bg-slate-100 rounded-full border border-slate-200 shadow-2xs">
          <div className="h-8 w-28 bg-white rounded-full shadow-xs animate-pulse" />
          <div className="h-8 w-28 bg-transparent rounded-full animate-pulse" />
        </div>
      </section>

      {/* 2. Free Trial / Active Banner Skeleton */}
      <div className="mb-6 p-4 sm:p-5 rounded-2xl bg-white border border-teal-100/80 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start sm:items-center gap-3.5 w-full">
          <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-100/70 flex items-center justify-center shrink-0 shadow-2xs">
            <div className="w-5 h-5 rounded-full bg-[#067978]/20 animate-pulse" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="h-4 w-44 bg-slate-200 rounded-md animate-pulse" />
              <div className="h-4 w-32 bg-teal-50 border border-teal-100/80 rounded-full animate-pulse" />
            </div>
            <div className="h-3 w-3/4 max-w-xl bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
      </div>

      {/* 3. 4 Pricing Cards Grid Skeleton */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {[1, 2, 3, 4].map((idx) => {
          const isHighlighted = idx === 2; // Mimic the recommended Pro tier
          return (
            <article
              key={idx}
              className={`relative flex flex-col rounded-2xl border bg-white p-5 shadow-sm ${
                isHighlighted
                  ? 'border-[#067978]/40 border-2 shadow-[0_8px_22px_rgba(6,121,120,0.08)]'
                  : 'border-slate-200'
              }`}
            >
              {/* Badge for highlighted card */}
              {isHighlighted && (
                <div className="absolute -top-3 left-5 rounded-full px-3 py-1 bg-[#067978]/20 border border-[#067978]/30 h-5 w-24 animate-pulse" />
              )}

              {/* Card Header (Title & Description) */}
              <div className="min-h-[100px] pt-1 space-y-2.5">
                <div className="h-6 w-28 bg-slate-200 rounded-lg animate-pulse" />
                <div className="h-3.5 w-full bg-slate-100 rounded animate-pulse" />
                <div className="h-3.5 w-4/5 bg-slate-100 rounded animate-pulse" />
              </div>

              {/* Price Area */}
              <div className="border-y border-slate-100 py-4 my-4 space-y-2">
                <div className="flex items-baseline gap-2">
                  <div className="h-8 w-28 bg-[#067978]/15 rounded-lg animate-pulse" />
                  <div className="h-3.5 w-12 bg-slate-100 rounded animate-pulse" />
                </div>
                <div className="h-3.5 w-36 bg-slate-200/80 rounded animate-pulse mt-1" />
              </div>

              {/* Feature List (5 rows) */}
              <ul className="space-y-3 flex-1 mb-5">
                {[1, 2, 3, 4, 5].map((item) => (
                  <li key={item} className="flex items-center gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-teal-100/70 shrink-0 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-[#067978]/40 animate-pulse" />
                    </div>
                    <div className="h-3.5 flex-1 bg-slate-100 rounded animate-pulse" />
                  </li>
                ))}
              </ul>

              {/* Action Button */}
              <div
                className={`w-full py-2.5 h-10 rounded-2xl border-2 animate-pulse ${
                  isHighlighted
                    ? 'bg-[#067978]/15 border-[#067978]/30'
                    : 'bg-slate-50 border-slate-200'
                }`}
              />
            </article>
          );
        })}
      </section>

      {/* 4. Comparison Table Skeleton */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden mb-8">
        <div className="px-5 sm:px-6 py-5 border-b border-slate-100">
          <div className="h-6 w-48 bg-slate-200 rounded-lg animate-pulse" />
          <div className="h-3.5 w-72 bg-slate-100 rounded mt-2 animate-pulse" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            {/* Table Head */}
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-5 sm:px-6 py-3 min-w-[200px]">
                  <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
                </th>
                {['Starter', 'Pro', 'Growth', 'Enterprise'].map((col) => (
                  <th key={col} className="px-4 py-3 min-w-[120px]">
                    <div className="h-4 w-16 bg-slate-200 rounded mx-auto animate-pulse" />
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-slate-100">
              {/* Category Header Row 1 */}
              <tr>
                <td colSpan={5} className="px-5 sm:px-6 py-2.5 bg-teal-50/70">
                  <div className="h-3.5 w-44 bg-[#067978]/20 rounded animate-pulse" />
                </td>
              </tr>

              {/* Feature Rows */}
              {[1, 2, 3, 4, 5].map((row) => (
                <tr key={row} className="hover:bg-slate-50/50">
                  <td className="px-5 sm:px-6 py-3.5">
                    <div className="h-3.5 w-48 bg-slate-100 rounded animate-pulse" />
                  </td>
                  {[1, 2, 3, 4].map((c) => (
                    <td key={c} className="px-4 py-3.5 text-center">
                      <div className="w-4 h-4 rounded-full bg-teal-100/60 mx-auto animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}

              {/* Category Header Row 2 */}
              <tr>
                <td colSpan={5} className="px-5 sm:px-6 py-2.5 bg-teal-50/70">
                  <div className="h-3.5 w-52 bg-[#067978]/20 rounded animate-pulse" />
                </td>
              </tr>

              {/* Feature Rows 2 */}
              {[1, 2, 3, 4].map((row) => (
                <tr key={row} className="hover:bg-slate-50/50">
                  <td className="px-5 sm:px-6 py-3.5">
                    <div className="h-3.5 w-40 bg-slate-100 rounded animate-pulse" />
                  </td>
                  {[1, 2, 3, 4].map((c) => (
                    <td key={c} className="px-4 py-3.5 text-center">
                      <div className="w-4 h-4 rounded-full bg-teal-100/60 mx-auto animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </PageContainer>
  );
};

export default SubscriptionSkeleton;
